"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Upload, Download, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";
import type { SecurityRef } from "@/lib/data/types";
import { parseCsvText } from "@/lib/portfolio/import/csv-parsing";
import { parseDividendCsvRows, type ParsedDividendRow } from "@/lib/dividend-tracker/import/parse-dividend-csv";
import { parseDividendReceiptText, type DividendReceiptGuess } from "@/lib/dividend-tracker/import/parse-dividend-receipt-text";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

interface PreviewRow extends ParsedDividendRow {
  resolved?: SecurityRef;
  resolving: boolean;
  /** Nur bei PDF-Belegen gesetzt (Feedback-Runde 19 C/13) - zeigt den Dateinamen statt
   * "Zeile N" in der Vorschau, da PDF-Zeilen keiner Tabellenzeile entsprechen. */
  sourceLabel?: string;
}

type Step = "upload" | "preview";

/** Schuetzt vor versehentlichem Massen-Upload (jede Datei loest einen eigenen
 * PDF.js-Parse-Durchlauf + /api/search-Request aus) - grosszuegig genug fuer einen
 * realistischen Beleg-Stapel (z.B. ein ganzes Jahr Trade-Republic-Ertragsabrechnungen). */
const MAX_PDF_FILES = 25;

/** Wandelt einen PDF-Beleg-Vorschlag in dieselbe ParsedDividendRow-Form um wie eine
 * CSV-/Excel-Zeile (Feedback-Runde 19 C/13) - dieselben drei Pflichtfeld-Regeln wie
 * parseDividendCsvRows() (lib/dividend-tracker/import/parse-dividend-csv.ts), damit
 * beide Importwege identisch validiert werden und dieselbe Vorschau-/Speicherlogik
 * unveraendert weiterverwenden koennen. ISIN wird vor dem loseren companyGuess
 * bevorzugt (siehe DividendReceiptGuess.isin-Kommentar). */
function guessToPreviewRow(guess: DividendReceiptGuess, fileName: string, rowNumber: number): PreviewRow {
  const symbol = guess.isin ?? guess.companyGuess;
  const currency = guess.currency ?? "EUR";

  let error: string | undefined;
  if (!symbol) error = "Keine ISIN/kein Ticker im Beleg gefunden - bitte einzeln über \"Dividende eintragen\" erfassen.";
  else if (guess.amount === undefined || guess.amount <= 0) error = "Betrag fehlt oder ist ungültig.";
  else if (!guess.paymentDate) error = "Datum fehlt oder ist ungültig.";
  else if (guess.paymentDate > new Date().toISOString().slice(0, 10)) error = "Datum liegt in der Zukunft.";

  return {
    rowNumber,
    sourceLabel: fileName,
    symbol,
    amount: guess.amount,
    currency,
    paymentDate: guess.paymentDate,
    isGross: guess.isGross ?? false,
    error,
    resolving: false,
  };
}

/** Echte, formatierte .xlsx-Vorlage statt der frueheren einfachen CSV-Datei (Erik-
 * Feedback 2026-07-23, Feedback-Runde 17 Punkt 28) - Upload akzeptiert weiterhin auch
 * .csv (siehe accept-Attribut unten), fuer Nutzer mit bereits vorhandenen CSV-Exporten
 * aus anderen Tools. */
async function downloadTemplate() {
  // Dynamischer Import statt statischem Top-Level-Import (Bundle-Size-Audit, 2026-08-10) -
  // `dividend-xlsx.ts` zieht `exceljs` (~1 MB) nach, das faellt sonst in JEDES initiale
  // Seiten-Bundle, das diesen Dialog rendert, obwohl es nur bei tatsaechlichem Excel-
  // Import/Vorlagen-Download gebraucht wird.
  const { buildDividendXlsxTemplate } = await import("@/lib/dividend-tracker/import/dividend-xlsx");
  const blob = await buildDividendXlsxTemplate();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "erikonomie-dividenden-vorlage.xlsx";
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Dividenden-Massenimport per CSV (backlog.md -> "Excel-Import/-Vorlage", Erik-Feedback
 * 2026-07-21) - eigenständiger Baustein, unabhängig vom ebenfalls offenen PDF/OCR-Weg
 * für einzelne Belege. Anders als der Depot-CSV-Import (csv-import-dialog.tsx) gibt es
 * hier kein fremdes Broker-Format zu erkennen: die Datei folgt Erikonomies eigener,
 * herunterladbarer Vorlage (feste Spalten, siehe parse-dividend-csv.ts), daher keine
 * Mapping-UI nötig. Gleiches Grundprinzip wie beim Depot-Import bleibt aber bestehen:
 * jede Zeile wird vor dem Speichern in einer Vorschau gezeigt, fehlerhafte Zeilen werden
 * sichtbar markiert und vom Speichern ausgeschlossen statt stillschweigend geraten.
 */
export function ImportDividendsDialog() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("upload");
  const [error, setError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  function reset() {
    setStep("upload");
    setError(null);
    setPreviewRows([]);
    setIsDragging(false);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  /** Loest jede Zeile mit gueltigem Symbol/ISIN gegen die bestehende globale Suche auf
   * (dieselbe Route wie TickerCombobox) - fuer CSV-/Excel- UND PDF-Zeilen identisch, da
   * beide dieselbe ParsedDividendRow-Form nutzen. Zeigt Zwischenstand sofort (resolving),
   * ergaenzt Treffer nach und nach statt auf alle Netzwerk-Antworten zu warten. */
  async function resolveRows(parsed: PreviewRow[]) {
    setPreviewRows(parsed.map((row) => ({ ...row, resolving: !row.error })));
    setStep("preview");

    const resolved = await Promise.all(
      parsed.map(async (row) => {
        if (row.error || !row.symbol) return undefined;
        try {
          const res = await fetch(`/api/search?q=${encodeURIComponent(row.symbol)}`);
          if (!res.ok) return undefined;
          const results = (await res.json()) as SecurityRef[];
          return results[0];
        } catch {
          return undefined;
        }
      }),
    );
    setPreviewRows(parsed.map((row, i) => ({ ...row, resolved: resolved[i], resolving: false })));
  }

  async function handleSpreadsheetFile(file: File) {
    const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
    const parsedFile = isXlsx
      ? await (await import("@/lib/dividend-tracker/import/dividend-xlsx")).parseDividendXlsxFile(file)
      : parseCsvText(await file.text());
    if (!parsedFile || parsedFile.headers.length === 0) {
      setError(
        isXlsx
          ? "Die Datei konnte nicht gelesen werden – ist es eine gültige Excel-Datei (.xlsx)?"
          : "Die Datei konnte nicht gelesen werden – ist es eine gültige CSV-Datei?",
      );
      return;
    }
    const { headers, rows } = parsedFile;
    const parsed = parseDividendCsvRows(headers, rows);
    if (!parsed) {
      setError(
        "Spalten nicht erkannt – bitte die heruntergeladene Vorlage verwenden (Symbol, Betrag und Zahlungsdatum sind Pflichtspalten).",
      );
      return;
    }
    if (parsed.length === 0) {
      setError("Die Datei enthält keine Zeilen.");
      return;
    }
    await resolveRows(parsed.map((row) => ({ ...row, resolving: false })));
  }

  /** Mehrfach-PDF-Import (Feedback-Runde 19 C/13, 2026-07-28 - vorher nur eine einzelne
   * Datei per Dateiauswahl, kein Drag & Drop, keine Mehrfachauswahl möglich). Jede PDF
   * wird unabhängig geparst (dieselbe Heuristik wie im Einzeleintrag-Dialog,
   * add-dividend-dialog.tsx) - ein fehlerhafter/ungültiger Beleg blockiert nicht die
   * übrigen, sondern erscheint als eigene Fehler-Zeile in der Vorschau. */
  async function handlePdfFiles(files: File[]) {
    if (files.length > MAX_PDF_FILES) {
      setError(`Bitte maximal ${MAX_PDF_FILES} PDFs auf einmal hochladen (aktuell ${files.length}).`);
      return;
    }
    const rows = await Promise.all(
      files.map(async (file, i): Promise<PreviewRow> => {
        try {
          const { extractPdfText } = await import("@/lib/dividend-tracker/import/extract-pdf-text");
          const text = await extractPdfText(file);
          const guess = parseDividendReceiptText(text);
          return guessToPreviewRow(guess, file.name, i + 1);
        } catch {
          return {
            rowNumber: i + 1,
            sourceLabel: file.name,
            currency: "EUR",
            isGross: false,
            resolving: false,
            error: "PDF konnte nicht gelesen werden - vermutlich kein Text enthalten (gescanntes Bild) oder beschädigt.",
          };
        }
      }),
    );
    await resolveRows(rows);
  }

  async function handleFiles(fileList: FileList | File[]) {
    setError(null);
    const files = Array.from(fileList);
    if (files.length === 0) return;

    const pdfFiles = files.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    // Sobald mindestens eine PDF dabei ist, gilt der ganze Stapel als PDF-Import -
    // ein gemischter Drop (z.B. versehentlich Vorlage + Belege zusammen) ist kein
    // sinnvoller reeller Anwendungsfall, eindeutiges Verhalten ist hier wichtiger als
    // stillschweigend zu erraten, was gemeint war.
    if (pdfFiles.length > 0) {
      await handlePdfFiles(pdfFiles);
      return;
    }
    if (files.length > 1) {
      setError("Bitte nur eine Excel-/CSV-Datei auf einmal hochladen (für mehrere Belege: PDF-Dateien nutzen).");
      return;
    }
    await handleSpreadsheetFile(files[0]);
  }

  async function handleConfirm() {
    setIsSaving(true);
    setError(null);

    const toInsert = previewRows.filter((row) => !row.error && row.resolved);
    if (toInsert.length === 0) {
      setError("Keine gültigen Zeilen zum Speichern.");
      setIsSaving(false);
      return;
    }

    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setError("Bitte melde dich erneut an.");
      setIsSaving(false);
      return;
    }

    const { error: insertError } = await supabase.from("dividend_events").insert(
      toInsert.map((row) => ({
        user_id: user.id,
        symbol: row.resolved!.symbol,
        company_name: row.resolved!.name,
        amount: row.amount!,
        currency: row.currency,
        payment_date: row.paymentDate!,
        is_gross: row.isGross,
      })),
    );

    setIsSaving(false);
    if (insertError) {
      setError("Einträge konnten nicht gespeichert werden.");
      return;
    }

    handleOpenChange(false);
    router.refresh();
  }

  // [Punkt 16, Rest] Brutto/Netto pro Zeile VOR dem Speichern korrigierbar - vorher aus
  // der Heuristik (parseDividendReceiptText/parseDividendCsvRows) nur angezeigt, nicht
  // mehr aenderbar, anders als beim manuellen Einzeleintrag (add-dividend-dialog.tsx).
  // Gleiche "Null-Fehler-Toleranz" fuer den Dividendenbetrag (backlog.md [KRITISCH, Punkt
  // 16]) - die Heuristik bleibt unangetastet, das hier ist nur ein manueller Korrekturweg,
  // falls sie doch einmal danebenliegt.
  function toggleRowIsGross(rowNumber: number, isGross: boolean) {
    setPreviewRows((rows) => rows.map((row) => (row.rowNumber === rowNumber ? { ...row, isGross } : row)));
  }

  const validRows = previewRows.filter((r) => !r.error);
  const resolvedCount = previewRows.filter((r) => !r.error && r.resolved).length;
  const errorCount = previewRows.length - validRows.length;
  const unresolvedCount = validRows.length - resolvedCount - validRows.filter((r) => r.resolving).length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline">
          <Upload />
          Dividenden importieren
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Dividenden aus Datei importieren</DialogTitle>
          <DialogDescription>
            Für mehrere Dividenden auf einmal statt einzeln einzutragen. Entweder die
            Vorlage herunterladen, in Excel/Sheets ausfüllen und wieder hochladen (die
            &bdquo;Firma&ldquo;-Spalte akzeptiert Firmenname, Ticker oder WKN - z. B.
            reicht &bdquo;Apple&ldquo;), oder mehrere Beleg-PDFs auf einmal hierher ziehen
            - jede PDF wird einzeln automatisch ausgelesen.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="flex flex-col gap-3">
            <Button type="button" variant="outline" size="sm" className="w-fit gap-1.5" onClick={downloadTemplate}>
              <Download className="h-3.5 w-3.5" />
              Vorlage herunterladen
            </Button>
            <label
              htmlFor="dividend-csv-file-input"
              onDragOver={(e) => {
                // Ohne preventDefault() erlaubt der Browser hier grundsaetzlich kein
                // onDrop (Feedback-Runde 19 Punkt 11: die gestrichelte Umrandung sah
                // bisher wie eine Dropzone aus, war aber nur ein verstecktes <input>).
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
              }}
              className={cn(
                "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors",
                isDragging ? "border-primary bg-muted" : "border-hairline bg-muted/40 hover:border-primary hover:bg-muted",
              )}
            >
              <Upload className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm font-semibold text-ink">
                {isDragging ? "Dateien hier ablegen" : "Datei(en) auswählen"}
              </span>
              <span className="text-xs text-muted-foreground">
                Ausgefüllte Vorlage (Excel oder CSV) oder mehrere Beleg-PDFs hierher ziehen
                oder anklicken
              </span>
            </label>
            <input
              id="dividend-csv-file-input"
              type="file"
              multiple
              accept=".xlsx,.csv,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/pdf"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) void handleFiles(e.target.files);
                e.target.value = "";
              }}
              className="sr-only"
            />
            {error && <p className="text-xs text-negative">{error}</p>}
          </div>
        )}

        {step === "preview" && (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-muted-foreground">
              {resolvedCount} von {previewRows.length} Zeilen bereit zum Speichern
              {unresolvedCount > 0 ? `, ${unresolvedCount} ohne Aktien-Zuordnung gefunden` : ""}
              {errorCount > 0 ? `, ${errorCount} mit fehlerhaften Angaben` : ""}. Fehlerhafte Zeilen
              werden beim Speichern übersprungen.
            </p>
            <div className="flex flex-col gap-2">
              {previewRows.map((row) => (
                <div
                  key={row.rowNumber}
                  className="rounded-md border border-hairline p-2 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      {row.sourceLabel && <FileText className="h-3 w-3 shrink-0" />}
                      {row.sourceLabel ?? `Zeile ${row.rowNumber}`}
                    </span>
                    {!row.error && row.amount !== undefined && (
                      <span className="flex items-center gap-1.5">
                        <span className="font-tabular text-ink">{formatCurrency(row.amount, row.currency)}</span>
                        <span className="flex shrink-0 gap-0.5 rounded-full bg-muted p-0.5 text-[0.65rem]">
                          <button
                            type="button"
                            onClick={() => toggleRowIsGross(row.rowNumber, false)}
                            className={cn(
                              "rounded-full px-1.5 py-0.5 font-medium transition-colors",
                              !row.isGross ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                            )}
                          >
                            Netto
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleRowIsGross(row.rowNumber, true)}
                            className={cn(
                              "rounded-full px-1.5 py-0.5 font-medium transition-colors",
                              row.isGross ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                            )}
                          >
                            Brutto
                          </button>
                        </span>
                      </span>
                    )}
                  </div>
                  {row.error ? (
                    <p className="text-negative">{row.error}</p>
                  ) : row.resolving ? (
                    <span className="text-muted-foreground">Wird zugeordnet…</span>
                  ) : row.resolved ? (
                    <>
                      <span className="font-medium text-ink">{row.resolved.name}</span>{" "}
                      <span className="text-muted-foreground">({row.resolved.symbol})</span>
                    </>
                  ) : (
                    <span className="text-negative">Keine Zuordnung gefunden ({row.symbol})</span>
                  )}
                </div>
              ))}
            </div>
            {error && <p className="text-xs text-negative">{error}</p>}
            <Button type="button" onClick={handleConfirm} disabled={isSaving || resolvedCount === 0}>
              {isSaving ? "Einen Moment…" : `${resolvedCount} Eintrag/Einträge speichern`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
